import { Injectable, inject } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { Store, StoreEvent, publishStoreEvent } from 'signalstory';
import { reportVersionReloadProblem } from '../../version-reload-diagnostics';
import {
  AppVersionUpdatedPayload,
  ManualReloadRequiredPayload,
  NewVersionDeployedPayload,
  appVersionUpdated,
  manualReloadRequired,
  newVersionDeployed,
} from '../events/app-version.event';

export interface AppVersionModel {
  /** Version baked into the running bundle. Empty means the check is disabled. */
  running: string;
  /** URL of the deployed version file. */
  versionUrl: string;
  /** Last version seen in the deployed version file. */
  deployed: string | null;
  /** Set when this page load was caused by the version check reloading an older bundle. */
  updatedFrom: string | null;
  /** Set when a reload for the deployed version already happened and did not take. */
  manualReloadRequired: ManualReloadRequiredPayload | null;
  /** True once a reload has been requested, so concurrent triggers do not reload twice. */
  reloading: boolean;
}

/** Marker left just before an automatic reload; consumed by the next page load. */
const PENDING_KEY = 'ami.version-reload.pending';
/** The deployed version we last reloaded for; the loop breaker. */
const LAST_RELOADED_FOR_KEY = 'ami.version-reload.last-reloaded-for';

/**
 * Holds the running vs deployed version and reacts to version events:
 * - newVersionDeployed: reload once per deployed version. A second mismatch for the same version
 *   means the reload did not fetch a newer bundle (for example the served version file and the
 *   bundle disagree), so instead of looping the store publishes manualReloadRequired.
 * - appVersionUpdated: record the version we came from so the app can tell the user.
 *
 * Nothing in here throws; problems are reported through reportVersionReloadProblem.
 */
@Injectable({ providedIn: 'root' })
export class AppVersionStore extends Store<AppVersionModel> {
  private readonly appInsights = inject(ApplicationInsights, { optional: true });

  constructor() {
    super({
      name: 'AppVersion',
      initialState: {
        running: '',
        versionUrl: '/assets/amiVersion.json',
        deployed: null,
        updatedFrom: null,
        manualReloadRequired: null,
        reloading: false,
      },
    });
    this.registerHandlers();
  }

  initialize(running: string, versionUrl: string) {
    this.mutate((state) => {
      state.running = running;
      state.versionUrl = versionUrl;
    });
  }

  /**
   * If the previous page load left a reload marker, clear it and publish appVersionUpdated.
   * Returns the payload so the caller can react synchronously (e.g. queue a toast).
   */
  consumeReloadMarker(): AppVersionUpdatedPayload | null {
    try {
      const raw = window.sessionStorage.getItem(PENDING_KEY);
      if (!raw) return null;
      window.sessionStorage.removeItem(PENDING_KEY);

      const payload = JSON.parse(raw) as AppVersionUpdatedPayload;
      if (!payload?.to) return null;

      if (payload.to !== this.state().running) {
        // We reloaded to pick up payload.to but are still running something else. Do not
        // announce an update that did not happen; the next check decides what to do.
        return null;
      }

      publishStoreEvent(appVersionUpdated, payload);
      return payload;
    } catch (error) {
      reportVersionReloadProblem(this.appInsights, 'Could not read the reload marker', error);
      return null;
    }
  }

  private registerHandlers() {
    this.registerHandler(newVersionDeployed, this.handleNewVersionDeployed);
    this.registerHandler(appVersionUpdated, this.handleAppVersionUpdated);
    this.registerHandler(manualReloadRequired, this.handleManualReloadRequired);
  }

  private handleNewVersionDeployed(store: this, event: StoreEvent<NewVersionDeployedPayload>) {
    const payload = event.payload;
    if (!payload || store.state().reloading) return;

    try {
      store.mutate((state) => {
        state.deployed = payload.deployed;
      });

      if (readStorage(LAST_RELOADED_FOR_KEY) === payload.deployed) {
        // Loop breaker: we already reloaded for this version and are still on the old bundle.
        if (!store.state().manualReloadRequired) {
          reportVersionReloadProblem(store.appInsights,
            `Already reloaded for deployed version "${payload.deployed}" but still running "${payload.running}"; not reloading again`,
            undefined, { running: payload.running, deployed: payload.deployed, reason: payload.reason });
          publishStoreEvent(manualReloadRequired, { running: payload.running, deployed: payload.deployed });
        }
        return;
      }

      store.mutate((state) => {
        state.reloading = true;
      });
      writeStorage(LAST_RELOADED_FOR_KEY, payload.deployed);
      writeStorage(PENDING_KEY, JSON.stringify({ from: payload.running, to: payload.deployed } satisfies AppVersionUpdatedPayload));

      console.log(`[version-reload] Deployed version "${payload.deployed}" differs from running "${payload.running}" (${payload.reason}); reloading.`);
      window.location.reload();
    } catch (error) {
      store.mutate((state) => {
        state.reloading = false;
      });
      reportVersionReloadProblem(store.appInsights, 'Reload for a new version failed', error,
        { running: payload.running, deployed: payload.deployed, reason: payload.reason });
    }
  }

  private handleAppVersionUpdated(store: this, event: StoreEvent<AppVersionUpdatedPayload>) {
    store.mutate((state) => {
      state.updatedFrom = event.payload?.from ?? null;
    });
  }

  private handleManualReloadRequired(store: this, event: StoreEvent<ManualReloadRequiredPayload>) {
    store.mutate((state) => {
      state.manualReloadRequired = event.payload ?? null;
    });
  }
}

function readStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode, quota): the reload still happens, just without markers.
  }
}
